import { useCallback, useEffect, useState } from "react";

import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";

import SoftBox from "components/SoftBox";
import SoftBadge from "components/SoftBadge";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { useAuth } from "context/AuthContext";
import {
  getInvoiceHistory,
  getSubscriptionStatus,
  syncPaymentSession,
} from "layouts/dashboardLayout/pages/Users/services/graphqlService";

const formatDate = (value) => {
  if (!value) return "Not available";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not available";
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatAmount = (invoice) => {
  const amount = invoice.amountPaid ?? invoice.amountDue;
  if (amount == null) return "Not available";
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: String(invoice.currency || "usd").toUpperCase(),
  }).format(Number(amount) / 100);
};

function PaymentsPage() {
  const { user, tokenEntityId, tokenProfileId } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSyncedCheckout, setHasSyncedCheckout] = useState(false);

  const loadBilling = useCallback(async () => {
    if (!user?.id_token || !tokenEntityId || !tokenProfileId) return;

    try {
      setIsLoading(true);
      setError(null);
      const params = new URLSearchParams(window.location.search);
      const sessionId = params.get("session_id");
      if (params.get("checkout") === "success" && sessionId && !hasSyncedCheckout) {
        const syncResult = await syncPaymentSession(sessionId, user.id_token);
        if (syncResult.data) {
          setSubscription(syncResult.data);
        }
        setHasSyncedCheckout(true);
      }
      const [statusResult, invoiceResult] = await Promise.all([
        getSubscriptionStatus(tokenEntityId, user.id_token),
        getInvoiceHistory(tokenProfileId, user.id_token, 10),
      ]);
      setSubscription(statusResult.data);
      setInvoices(invoiceResult.data || []);
    } catch (err) {
      setError(err.message || "Could not load payment details.");
    } finally {
      setIsLoading(false);
    }
  }, [hasSyncedCheckout, tokenEntityId, tokenProfileId, user?.id_token]);

  useEffect(() => {
    loadBilling();
  }, [loadBilling]);

  return (
    <SoftBox py={3}>
      <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <SoftBox>
          <SoftTypography variant="h4" fontWeight="bold" gutterBottom>
            Payments
          </SoftTypography>
          <SoftTypography variant="body2" color="text">
            Subscription status and Stripe invoice history.
          </SoftTypography>
        </SoftBox>
        <SoftButton variant="outlined" color="dark" onClick={loadBilling} disabled={isLoading}>
          <Icon sx={{ fontWeight: "bold" }}>refresh</Icon>&nbsp;
          Refresh
        </SoftButton>
      </SoftBox>

      {new URLSearchParams(window.location.search).get("checkout") === "success" && (
        <SoftBox mb={2} p={2} bgColor="success" borderRadius="lg">
          <SoftTypography variant="body2" color="white">
            Checkout completed. Your subscription status is syncing now.
          </SoftTypography>
        </SoftBox>
      )}

      {error && (
        <SoftBox mb={2} p={2} bgColor="error" borderRadius="lg">
          <SoftTypography variant="body2" color="white">
            {error}
          </SoftTypography>
        </SoftBox>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          <Card>
            <SoftBox p={3}>
              <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <SoftTypography variant="h6" fontWeight="bold">
                  Subscription
                </SoftTypography>
                <SoftBadge
                  variant="contained"
                  color={subscription?.status === "active" ? "success" : "secondary"}
                  size="sm"
                  badgeContent={subscription?.status || "not subscribed"}
                  container
                  circular
                />
              </SoftBox>

              <SoftTypography variant="caption" color="text">
                Plan
              </SoftTypography>
              <SoftTypography variant="h5" fontWeight="bold" mb={2}>
                {subscription?.planCode || "Free tier"}
              </SoftTypography>

              <SoftTypography variant="caption" color="text">
                Renewal date
              </SoftTypography>
              <SoftTypography variant="body2" fontWeight="medium">
                {formatDate(subscription?.currentPeriodEnd)}
              </SoftTypography>
            </SoftBox>
          </Card>
        </Grid>

        <Grid item xs={12} md={7}>
          <Card>
            <SoftBox p={3}>
              <SoftTypography variant="h6" fontWeight="bold" mb={2}>
                Invoices
              </SoftTypography>
              {invoices.length === 0 ? (
                <SoftTypography variant="body2" color="text">
                  No Stripe invoices found yet.
                </SoftTypography>
              ) : (
                <SoftBox display="grid" gap={1.5}>
                  {invoices.map((invoice) => (
                    <SoftBox
                      key={invoice.stripeInvoiceId}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      p={1.5}
                      borderRadius="lg"
                      sx={{ border: "1px solid", borderColor: "grey.200" }}
                    >
                      <SoftBox>
                        <SoftTypography variant="button" fontWeight="medium">
                          {invoice.stripeInvoiceId}
                        </SoftTypography>
                        <SoftTypography variant="caption" color="text" display="block">
                          {formatDate(invoice.paidAt || invoice.createdAt)}
                        </SoftTypography>
                      </SoftBox>
                      <SoftBox textAlign="right">
                        <SoftTypography variant="button" fontWeight="bold">
                          {formatAmount(invoice)}
                        </SoftTypography>
                        {invoice.hostedInvoiceUrl && (
                          <SoftTypography
                            component="a"
                            href={invoice.hostedInvoiceUrl}
                            target="_blank"
                            rel="noreferrer"
                            variant="caption"
                            color="info"
                            display="block"
                          >
                            View invoice
                          </SoftTypography>
                        )}
                      </SoftBox>
                    </SoftBox>
                  ))}
                </SoftBox>
              )}
            </SoftBox>
          </Card>
        </Grid>
      </Grid>
    </SoftBox>
  );
}

export default PaymentsPage;
